import React, { useState } from 'react';
import { 
  User,
  Mail,
  Phone,
  Globe,
  Award,
  Calendar,
  Edit2,
  Settings,
  Lock,
  LogOut
} from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

const Profile = () => {
  const [activeSection, setActiveSection] = useState('personal');
  
  // TODO: Replace with actual user data from API
  const userData = {
    name: "John Doe",
    email: "john.doe@example.com",
    phone: "+1 (555) 123-4567",
    location: "New York, USA",
    joinedDate: "January 2024",
    certifications: 3,
    avatar: "/api/placeholder/128/128"
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex gap-6">
        {/* Left Sidebar */}
        <div className="w-64 flex flex-col gap-4">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <nav className="space-y-1">
              <button
                onClick={() => setActiveSection('personal')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeSection === 'personal'
                    ? 'bg-navy-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <User size={20} />
                <span>Personal Info</span>
              </button>
              <button
                onClick={() => setActiveSection('preferences')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeSection === 'preferences'
                    ? 'bg-navy-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Settings size={20} />
                <span>Preferences</span>
              </button>
              <button
                onClick={() => setActiveSection('security')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeSection === 'security'
                    ? 'bg-navy-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Lock size={20} />
                <span>Security</span>
              </button>
              <button
                onClick={async () => {
                  const { error } = await supabase.auth.signOut();
                  if (!error) {
                    window.location.href = '/login';
                  }
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <LogOut size={20} />
                <span>Log Out</span>
              </button>
            </nav>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="font-medium text-gray-900 mb-4">Learning Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="bg-navy-50 p-2 rounded-lg">
                  <Award className="text-navy-600" size={20} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Certifications</p>
                  <p className="font-medium text-navy-900">{userData.certifications}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-navy-50 p-2 rounded-lg">
                  <Calendar className="text-navy-600" size={20} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Member Since</p>
                  <p className="font-medium text-navy-900">{userData.joinedDate}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-6 mb-8">
              <img
                src={userData.avatar}
                alt={userData.name}
                className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
              />
              <div>
                <h2 className="text-2xl font-bold text-navy-900">{userData.name}</h2>
                <p className="text-gray-600">Sales Professional</p>
                <button className="mt-3 text-navy-600 hover:text-navy-700 text-sm flex items-center gap-2">
                  <Edit2 size={16} />
                  Change Profile Picture
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <User size={20} className="text-gray-400" />
                  <input
                    type="text"
                    value={userData.name}
                    className="flex-1 border-none focus:ring-0 p-0 text-gray-900"
                    placeholder="Enter your full name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <Phone size={20} className="text-gray-400" />
                  <input
                    type="tel"
                    value={userData.phone}
                    className="flex-1 border-none focus:ring-0 p-0 text-gray-900"
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <Mail size={20} className="text-gray-400" />
                  <input
                    type="email"
                    value={userData.email}
                    className="flex-1 border-none focus:ring-0 p-0 text-gray-900"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <Globe size={20} className="text-gray-400" />
                  <input
                    type="text"
                    value={userData.location}
                    className="flex-1 border-none focus:ring-0 p-0 text-gray-900"
                    placeholder="Enter your location"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button className="bg-navy-600 text-white px-6 py-2 rounded-lg hover:bg-navy-700 transition-colors">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
